import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs'
import { Button } from '@shared/components/ui/button'
import { Badge } from '@shared/components/ui/badge'
import { SubscriptionTimeline } from './SubscriptionTimeline'
import { PaymentAnalytics } from './PaymentAnalytics'
import { SubscriptionManagement } from './SubscriptionManagement'
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  DollarSign,
  RefreshCw,
  Download
} from 'lucide-react'
import { apiUrl } from '@/lib/api'

interface TenantAnalyticsDashboardProps {
  tenantId: string
}

export const TenantAnalyticsDashboard: React.FC<TenantAnalyticsDashboardProps> = ({
  tenantId
}) => {
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [timelineEvents, setTimelineEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAnalyticsData()
  }, [tenantId])

  const loadAnalyticsData = async () => {
    setLoading(true)
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }

      // Load payment analytics
      const paymentResponse = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/analytics/payments`), { headers: requestHeaders })
      if (paymentResponse.ok) {
        const paymentData = await paymentResponse.json()
        setAnalyticsData(paymentData)
      }

      // Load timeline events
      const timelineResponse = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/analytics/timeline`), { headers: requestHeaders })
      if (timelineResponse.ok) {
        const timelineData = await timelineResponse.json()
        setTimelineEvents(timelineData.events || [])
      }
    } catch (error) {
      console.error('Failed to load analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const exportAnalytics = async () => {
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/analytics/export`), { headers: requestHeaders })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `tenant-${tenantId}-analytics-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    } catch (error) {
      console.error('Failed to export analytics:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tenant Analytics</h2>
          <p className="text-muted-foreground">Comprehensive subscription and payment insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadAnalyticsData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportAnalytics}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>


      {/* Main Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payments">Payment Analytics</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {analyticsData && (
            <div className="grid gap-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${((analyticsData.total_revenue || 0) / 100).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Lifetime Value</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${((analyticsData.lifetime_value || 0) / 100).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {analyticsData.payment_frequency === 'annual' ? 'Annual Revenue' : 'Monthly Revenue'}
                    </CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${((analyticsData.payment_frequency === 'annual' 
                        ? analyticsData.annual_recurring_revenue 
                        : analyticsData.monthly_recurring_revenue || 0) / 100).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(analyticsData.churn_rate || 0).toFixed(1)}%
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Overview Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total Payments</span>
                      <span className="font-medium">{analyticsData.total_payments || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Average Payment</span>
                      <span className="font-medium">
                        ${((analyticsData.average_payment || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Success Rate</span>
                      <span className="font-medium">
                        {(analyticsData.payment_success_rate || 0).toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Last Payment</span>
                      <span className="font-medium">
                        {analyticsData.last_payment_date 
                          ? new Date(analyticsData.last_payment_date).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Days Since Last Payment</span>
                      <span className="font-medium">{analyticsData.days_since_last_payment || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Refund Rate</span>
                      <span className="font-medium">
                        {(analyticsData.refund_rate || 0).toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="payments" className="space-y-6">
          {analyticsData && (
            <PaymentAnalytics 
              data={{
                total_revenue: analyticsData.total_revenue || 0,
                total_refunds: analyticsData.total_refunds || 0,
                net_revenue: analyticsData.net_revenue || 0,
                successful_payments: analyticsData.successful_payments || 0,
                failed_payments: analyticsData.failed_payments || 0,
                total_payments: analyticsData.total_payments || 0,
                average_payment: analyticsData.average_payment || 0,
                last_payment_date: analyticsData.last_payment_date,
                payment_success_rate: analyticsData.payment_success_rate || 0,
                refund_rate: analyticsData.refund_rate || 0
              }}
              loading={loading}
            />
          )}
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          <SubscriptionTimeline 
            events={timelineEvents}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="subscriptions" className="space-y-6">
          <SubscriptionManagement
            tenantId={tenantId}
            title="Subscriptions"
            description="Manage active subscriptions for this tenant"
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
